import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService, AuthStatus } from '../api/auth.service';
import { ProfileService } from '../api/profile.service';
import { TierLevel, User } from '../models/user';
import { LocaleService } from './locale.service';

declare var dataLayer: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  readonly id = environment.googleAnalytics.id;
  profile: User;

  statusChangesSubscription: Subscription;
  routeChangesSubscription: Subscription;

  constructor(
    private auth: AuthService,
    private profileService: ProfileService,
    private localeService: LocaleService,
    private router: Router,
    private titleService: Title
  ) { }

  init() {
    // Script is loaded in index.html for veriifcation reasons form Google side
    // this.appendScript();

    // Auth Changes
    this.statusChangesSubscription?.unsubscribe();
    this.statusChangesSubscription = this.auth.statusChanges.subscribe(status => {
      if (status === AuthStatus.LoggedIn) {
        this.getUser();
      } else if (status === AuthStatus.LoggedOff) {
        this.profile = null;
      }
    });

    if (this.auth.getToken()) {
      this.getUser();
    }

    // Route Changes
    this.routeChangesSubscription?.unsubscribe();
    this.routeChangesSubscription = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      timer(100).subscribe(() => {
        this.sendPageView(event.urlAfterRedirects, this.titleService.getTitle());
      });
    });
  }

  sendEvent(category: string, data: object = {}) {
    dataLayer.push({
      'event': category,
      ...data,
      ...this.userInfo()
    });
  }

  sendPageView(url: string, title: string) {
    const localePath = this.localeService.currentLocale;
    const noQueryPath = url ? url.split('?')[0] : '';

    dataLayer.push({
      'event': 'manual_pageview',
      'page_title': title,
      'page_path': `/${localePath}${noQueryPath}`,
      'page_url': `${window.location.origin}/${localePath}${url}`,
      ...this.userInfo()
    });
  }

  getUser() {
    this.profileService.getGeneral().subscribe(response => {
      this.profile = response;
      // dataLayer.push('set', this.userInfo);
    });
  }

  userInfo() {
    if (!this.profile) {
      return {
        user_id: null,
        country: null,
        is_verified: null,
        verification_tier: null
      };
    }

    return {
      user_id: this.auth.getToken().userId,
      country: this.profile.addresses && this.profile.addresses[0] && this.profile.addresses[0].country ? this.profile.addresses[0].country.code3 : null,
      is_verified: this.profile.verificationDetails && this.profile.verificationDetails.tier && this.profile.verificationDetails.tier !== TierLevel.NONE,
      verification_tier: this.profile.verificationDetails.tier
    }
  }

  appendScript() {
    try {
      const configScript = document.createElement('script');
      configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      `;
      document.head.appendChild(configScript);

      const tagManagerScript = document.createElement('script');
      tagManagerScript.async = true;
      tagManagerScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.id}`;
      document.head.appendChild(tagManagerScript);
    } catch (e) {
      console.error('Error appending google analytics');
      console.error(e);
    }
  }
}